/* =========================================================
   Pretty J — Wishlist engine (localStorage + account sync hook)
   - Stores product handles in localStorage key "pj_wishlist".
   - Auto-wires any wishlist button by class/attribute, deriving the
     product handle from a nearby product link when not provided.
   - Keeps a header counter ([data-pj-wish-count]) in sync.
   - Dispatches "pj-wishlist:change" on window for any listener.
   ========================================================= */
(function () {
  'use strict';

  var STORAGE_KEY = 'pj_wishlist';

  // Selectors that mark a wishlist toggle button in this theme.
  var BUTTON_SELECTORS = [
    '[data-pj-wish]',
    '.pjt__wish',
    '.vibe-wishlist-btn',
    '.pj-wish-btn'
  ];
  var BUTTON_SELECTOR = BUTTON_SELECTORS.join(',');

  /* ---------- storage ---------- */
  function normalizeHandle(item) {
    if (!item) return '';
    if (typeof item === 'string') return handleFromUrl(item) || item;
    if (typeof item === 'object') {
      return item.handle || item.product_handle || item.productHandle || handleFromUrl(item.url || item.href || '');
    }
    return '';
  }

  function normalizeList(arr) {
    var seen = {};
    return (Array.isArray(arr) ? arr : [])
      .map(normalizeHandle)
      .filter(function (handle) {
        if (!handle || seen[handle]) return false;
        seen[handle] = true;
        return true;
      });
  }

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return normalizeList(arr);
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    list = normalizeList(list);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {}
    emitChange(list);
  }

  function has(handle) {
    return read().indexOf(handle) !== -1;
  }

  function add(handle) {
    if (!handle) return;
    var list = read();
    if (list.indexOf(handle) === -1) {
      list.push(handle);
      write(list);
    }
  }

  function remove(handle) {
    if (!handle) return;
    var list = read().filter(function (h) { return h !== handle; });
    write(list);
  }

  function toggle(handle) {
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle);
    return true;
  }

  /* ---------- events ---------- */
  function emitChange(list) {
    window.dispatchEvent(new CustomEvent('pj-wishlist:change', { detail: { list: list || read() } }));
  }

  /* ---------- helpers ---------- */
  // Derive a product handle from a button: explicit attr first, else nearest product link.
  function handleFromButton(btn) {
    var explicit = btn.getAttribute('data-handle') || btn.getAttribute('data-product-handle');
    if (explicit) return explicit;

    // Look for a product link within the same card.
    var card = btn.closest(
      '.pjt__card-wrap, .pjt__card, .vibe-card, .pj-wish-card, [data-pj-wish-card], .grid__item, li, article, div'
    );
    var scope = card || document;
    var link = scope.querySelector('a[href*="/products/"]');
    if (!link) return '';
    return handleFromUrl(link.getAttribute('href'));
  }

  function handleFromUrl(url) {
    if (!url) return '';
    var m = url.split('?')[0].match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : '';
  }

  function syncButton(btn) {
    var handle = btn.__pjHandle || handleFromButton(btn);
    btn.__pjHandle = handle;
    var active = handle && has(handle);
    btn.classList.toggle('is-wished', !!active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    if (active) {
      btn.setAttribute('aria-label', 'Remove from wishlist');
    } else {
      btn.setAttribute('aria-label', 'Add to wishlist');
    }
  }

  function syncAllButtons(root) {
    (root || document).querySelectorAll(BUTTON_SELECTOR).forEach(syncButton);
  }

  function syncCount() {
    var n = read().length;
    document.querySelectorAll('[data-pj-wish-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('is-empty', n === 0);
      el.hidden = n === 0;
      el.setAttribute('aria-hidden', n === 0 ? 'true' : 'false');
    });
  }

  /* ---------- account sync hook (phase 2) ---------- */
  // If a logged-in customer id is exposed, this is where we'd POST/GET the
  // saved list to a customer metafield via an App Proxy. Left as a no-op hook.
  function syncToAccount() {
    if (!window.pjWishlistCustomerId) return;
    // Placeholder for App Proxy sync.
  }

  /* ---------- wiring ---------- */
  function onClick(e) {
    var btn = e.target.closest(BUTTON_SELECTOR);
    if (!btn) return;

    // Don't hijack real links.
    if (btn.tagName === 'A' && btn.getAttribute('href') && !btn.hasAttribute('data-pj-wish')) {
      // Allow header link to navigate normally.
      if (btn.hasAttribute('data-pj-wish-link')) return;
    }

    e.preventDefault();
    e.stopPropagation();

    var handle = btn.__pjHandle || handleFromButton(btn);
    btn.__pjHandle = handle;
    if (!handle) return;

    var nowActive = toggle(handle);
    btn.classList.toggle('is-wished', nowActive);
    btn.setAttribute('aria-pressed', nowActive ? 'true' : 'false');
    btn.classList.add('pj-wish-pop');
    setTimeout(function () { btn.classList.remove('pj-wish-pop'); }, 320);

    syncToAccount();
  }

  function init() {
    document.addEventListener('click', onClick);

    syncAllButtons();
    syncCount();

    window.addEventListener('pj-wishlist:change', function () {
      syncAllButtons();
      syncCount();
    });

    // Re-sync when sections re-render in the theme editor.
    document.addEventListener('shopify:section:load', function (e) {
      syncAllButtons(e.target);
      syncCount();
    });

    // Re-sync dynamically injected cards (e.g. vibe-grid JS render).
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            syncAllButtons();
            break;
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  // Public API.
  window.PJWishlist = {
    read: read,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    count: function () { return read().length; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* =========================================================
   Pretty J — Collection card extras (swatch image-swap, size pick, add to bag)
   ========================================================= */
(function () {
  'use strict';

  function cardMainImg(card) {
    var media = card.querySelector('.card__media');
    if (!media) return null;
    // The primary (non-secondary) image is the first <img> in the media block.
    return media.querySelector('img');
  }

  function onClick(e) {
    var dot = e.target.closest('.pj-cx__dot, .pjpc__dot');
    if (dot) {
      var card = dot.closest('.card-wrapper, .pjpc');
      card.querySelectorAll('.pj-cx__dot, .pjpc__dot').forEach(function (d) { d.classList.remove('is-active'); });
      dot.classList.add('is-active');
      var img = dot.getAttribute('data-img');
      var el = card.querySelector('.pjpc__img:not(.pjpc__img--hover)') || cardMainImg(card);
      if (img && el) {
        var pre = new Image();
        pre.onload = function () { el.removeAttribute('srcset'); el.src = img; };
        pre.src = img;
      }
      return;
    }

    var pill = e.target.closest('.pj-cx__pill, .pjpc__pill');
    if (pill) {
      var card2 = pill.closest('.card-wrapper, .pjpc');
      card2.querySelectorAll('.pj-cx__pill, .pjpc__pill').forEach(function (p) { p.classList.remove('is-active'); });
      pill.classList.add('is-active');
      var atc = card2.querySelector('[data-pj-card-atc]');
      if (atc && pill.dataset.variantId) atc.dataset.variantId = pill.dataset.variantId;
      return;
    }

    var addBtn = e.target.closest('[data-pj-card-atc]');
    if (addBtn && !addBtn.disabled) {
      e.preventDefault();
      addToCart(addBtn);
    }
  }

  function addToCart(btn) {
    var vid = btn.getAttribute('data-variant-id');
    if (!vid) return;
    var label = btn.querySelector('span');
    var original = label ? label.textContent : '';
    btn.disabled = true;
    if (label) label.textContent = 'Adding…';

    var sections = [];
    var drawer = document.querySelector('cart-drawer');
    if (drawer) sections.push('cart-drawer');
    if (document.querySelector('#cart-icon-bubble')) sections.push('cart-icon-bubble');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(vid), quantity: 1 }], sections: sections, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        if (label) label.textContent = 'Added ✓';
        if (drawer && typeof drawer.renderContents === 'function' && state.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(state);
          if (typeof drawer.open === 'function') drawer.open();
        } else if (state.sections && state.sections['cart-icon-bubble']) {
          var bubble = document.querySelector('#cart-icon-bubble');
          var html = new DOMParser().parseFromString(state.sections['cart-icon-bubble'], 'text/html');
          var nb = html.querySelector('#cart-icon-bubble');
          if (bubble && nb) bubble.innerHTML = nb.innerHTML;
        }
        setTimeout(function () { if (label) label.textContent = original; btn.disabled = false; }, 1600);
      })
      .catch(function () { if (label) label.textContent = original; btn.disabled = false; });
  }

  document.addEventListener('click', onClick);
})();

/* =========================================================
   Pretty J — Product card hover video (play on hover, pause/reset on leave)
   ========================================================= */
(function () {
  'use strict';

  function videoEl(card) {
    var wrap = card.querySelector('.pj-card-hover-video');
    if (!wrap) return null;
    return wrap.tagName === 'VIDEO' ? wrap : wrap.querySelector('video');
  }

  function play(e) {
    var card = e.currentTarget;
    var v = videoEl(card);
    if (!v) return;
    try { v.muted = true; var p = v.play(); if (p && p.catch) p.catch(function () {}); } catch (err) {}
  }

  function stop(e) {
    var card = e.currentTarget;
    var v = videoEl(card);
    if (!v) return;
    try { v.pause(); v.currentTime = 0; } catch (err) {}
  }

  function bind(root) {
    (root || document).querySelectorAll('.card-wrapper, .pjt__card-wrap, .pj-rec-card').forEach(function (card) {
      if (card.dataset.pjHoverVid === 'true') return;
      if (!card.querySelector('.pj-card-hover-video')) return;
      card.dataset.pjHoverVid = 'true';
      card.addEventListener('mouseenter', play);
      card.addEventListener('mouseleave', stop);
    });
  }

  function init() {
    bind(document);
    document.addEventListener('shopify:section:load', function (e) { bind(e.target); });
    if ('MutationObserver' in window) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (muts[i].addedNodes && muts[i].addedNodes.length) { bind(document); break; }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
